import * as Api from '@ViewModels';
import { IImageLibraryKeyword } from './types';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';
import { ImageLibraryKindFilter, toKindQueryParams } from './useImageLibraryQuery';

const IMAGE_LIBRARY_KEYWORDS_QUERY_KEY = 'imageLibrary/keywords';

/**
 * Every folder in use on the account, most-used first. Kept separate from the listing because the folder
 * rail has to show folders that none of the currently loaded page happens to be in.
 */
export function useImageLibraryKeywordsQuery({
	enabled = true,
	impersonationContext,
	kind,
}: {
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
	/** Must match the listing's, or a folder's count will not match what clicking it returns. */
	kind?: ImageLibraryKindFilter;
} = {}) {
	const userSession = useUserSession();

	return useQuery({
		enabled,
		meta: {
			errorLogging: { source: 'ImageLibraryKeywords' },
			toastErrorMessage: 'Failed to load your folders',
		},
		queryKey: [IMAGE_LIBRARY_KEYWORDS_QUERY_KEY, impersonationContext, kind],
		queryFn: () =>
			userSession.webServiceHelper.callAsync<IImageLibraryKeyword[]>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					queryParams: toKindQueryParams(kind),
					urlPath: 'imageLibrary/keywords',
				}),
				'GET'
			),
	});
}

export function invalidateImageLibraryKeywordsQuery() {
	return reactQueryClient.invalidateQueries({ queryKey: [IMAGE_LIBRARY_KEYWORDS_QUERY_KEY] });
}
